import React from 'react';
import {
	Avatar,
	Box,
	Paper,
	Typography,
	useTheme,
	Accordion,
	AccordionSummary,
	useMediaQuery,
	AccordionDetails,
	ButtonBase,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import stylesLogo from './Account.styles';
import AddNewButton from '../Buttons/AddNewButton';
import { useState, useContext } from 'react';
import AccountCheckboxContainer from './AccountCheckboxContainer';
import linkedAccountsFIStyles from './LinkedAccountsFI.styles';
import sizeFontCustom from '../Discovery/fontSize.styles';
import StepperContext from '../Context/StepperContext';
import { useTranslation } from '../../hooks/useTranslation';

function LinkedAccountsFI(props) {
	const { t } = useTranslation();
	const { prevStep } = useContext(StepperContext);
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

	// Check for dark theme
	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	const redirect = () => {
		global.fi = global.FIGroups?.[name];
		// Set flags to show ALL accounts but keep matched accounts' restrictions
		delete global.isTargetedDiscoveryMode;
		global.userClickedAddMore = true; // Allow discovering ANY accounts
		global.hasReachedConsent = true; // Mark that user already reached consent
		global.isAddAccount = true;
		props.history.push(
			`/home/?number=${global.mobileNumber}&isAddAccount=${true}`,
			{ fiTypes: global.FIGroups[name] },
		);
	};
	const {
		linkedAccounts,
		name,
		fIGroupsWithZeroAccts,
		consentHandle,
		superState,
	} = props;
	const handleAddAccount = () => {
		prevStep();
		redirect();
		return;
	};
	const [isExpanded, setIsExpanded] = useState(false);
	const isActiveFlag = props?.selectedConsentsState?.includes(
		props?.consentHandle,
	)
		? true
		: false;

	const selectedAccounts = superState?.consentSelectedAccounts?.[
		consentHandle
	]?.filter((account) => global.FIGroups?.[name]?.includes(account?.FIType));

	const requiredAccounts = linkedAccounts?.filter((account) =>
		global.FIGroups?.[name]?.includes(account?.FIType),
	);
	const accountsLinkedFlag = requiredAccounts?.length > 0;
	const isBankAccountsSection = name === 'Bank Accounts';
	const accordionAccountsDetails = !isBankAccountsSection
		? mobileScreen
			? 1
			: 3
		: requiredAccounts?.length
			? requiredAccounts?.length
			: fIGroupsWithZeroAccts?.length;

	return (
		<Paper
			sx={{
				...stylesLogo.paper,
				backgroundColor: isDark ? '#161616' : '#fff',
				color: isDark ? '#fff' : '#000',
			}}
		>
			<Box>
				<Box sx={linkedAccountsFIStyles.headingContainer}>
					<Typography
						noWrap
						variant="h6"
						sx={{
							...linkedAccountsFIStyles.name,
							color:
								props.customDetails?.primaryTextColor ??
								(isDark ? '#fff' : '#000'),
						}}
					>
						{name}
						{accountsLinkedFlag && (
							<Avatar
								sx={{
									...linkedAccountsFIStyles.count,
									bgcolor: isDark ? '#7A8185' : theme.palette.info.light,
									color: isDark ? '#161616' : '#81858F',
								}}
							>
								{requiredAccounts.length}
							</Avatar>
						)}
					</Typography>
					{accountsLinkedFlag && (
						<>
							<ButtonBase
								disabled={!isActiveFlag}
								disableRipple
								sx={{
									...sizeFontCustom.altHeading,
									color: !isActiveFlag
										? theme.palette.action.disabled
										: (props.customDetails?.primaryColor ??
											(isDark ? '#fff' : '#000')),
									display: 'flex',
									alignItems: 'center',
									gap: '0.5rem',
									'& .MuiSvgIcon-root': {
										fill: !isActiveFlag
											? theme.palette.action.disabled
											: (props.customDetails?.primaryColor ??
												theme.palette.primary.main),
									},
									'&:hover': {
										backgroundColor: 'transparent !important',
									},
									'&.Mui-expanded': {
										backgroundColor: 'transparent !important',
									},
								}}
								onClick={() =>
									props.selectUnselectAllFIAccounts(
										requiredAccounts,
										name,
										props.consentHandle,
									)
								}
							>
								Select All
								{selectedAccounts.length === requiredAccounts.length &&
								requiredAccounts.length > 0 ? (
									<CheckBoxIcon />
								) : selectedAccounts.length > 0 &&
								  selectedAccounts.length < requiredAccounts.length ? (
									<IndeterminateCheckBoxIcon />
								) : (
									<CheckBoxOutlineBlankIcon />
								)}
							</ButtonBase>
						</>
					)}
				</Box>
				<>
					{!isBankAccountsSection && accountsLinkedFlag ? (
						<>
							<Accordion
								elevation={0}
								expanded={isExpanded}
								sx={{
									...linkedAccountsFIStyles.accordian,
									backgroundColor: isDark ? 'transparent' : '#fff',
									'&::before': {
										display: 'none',
									},
									'&::after': {
										display: 'none',
									},
									// Target the specific MUI classes you found
									'&.MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPaper-elevation0.MuiAccordion-root.MuiAccordion-rounded.MuiAccordion-gutters':
										{
											backgroundColor: isDark
												? 'transparent'
												: '#fff !important',
										},
									'& .MuiButtonBase-root.MuiAccordionSummary-root.MuiAccordionSummary-gutters':
										{
											backgroundColor: isDark
												? 'transparent'
												: '#fff !important',
										},
									'& .MuiAccordionSummary-content.MuiAccordionSummary-contentGutters':
										{
											backgroundColor: isDark
												? 'transparent'
												: '#fff !important',
										},
									'& .MuiAccordionDetails-root': {
										backgroundColor: isDark ? 'transparent' : '#fff !important',
									},
								}}
							>
								<AccordionSummary
									onClick={() => {}}
									sx={{
										...linkedAccountsFIStyles.accordianSummary,
										backgroundColor: isDark ? 'transparent' : '#fff !important',
										color: isDark ? '#fff' : '#000',
										// Target the specific classes for AccordionSummary
										'&.MuiButtonBase-root.MuiAccordionSummary-root.MuiAccordionSummary-gutters':
											{
												backgroundColor: isDark
													? 'transparent'
													: '#fff !important',
											},
										'& .MuiAccordionSummary-content.MuiAccordionSummary-contentGutters':
											{
												backgroundColor: isDark
													? 'transparent'
													: '#fff !important',
											},
										'&:hover': {
											backgroundColor: isDark
												? 'transparent'
												: '#fff !important',
										},
										'&.Mui-expanded': {
											backgroundColor: isDark
												? 'transparent'
												: '#fff !important',
										},
									}}
									expandIcon={
										!isExpanded &&
										requiredAccounts.length > accordionAccountsDetails && (
											<ButtonBase
												disabled={!isActiveFlag}
												sx={{
													...sizeFontCustom.altHeading,
													color: !isActiveFlag
														? 'lightgray'
														: (props.customDetails?.primaryTextColor ??
															(isDark ? '#fff' : theme.palette.primary.main)),
													display: 'flex',
													alignItems: 'center',
													gap: '0.25rem',
												}}
												onClick={() => setIsExpanded(true)}
												color={'primary'}
											>
												{t('common.buttons.showMore')}
												<ExpandMoreIcon sx={{ fontSize: '1rem' }} />
											</ButtonBase>
										)
									}
									aria-controls="linkedAccountsPanel-content"
									id="linkedAccountsPanel-header"
								>
									<AccountCheckboxContainer
										requiredAccounts={requiredAccounts}
										isZero={false}
										start={0}
										end={accordionAccountsDetails}
										accountRestrictions={props.accountRestrictions}
										{...props}
									/>
								</AccordionSummary>
								<AccordionDetails
									sx={{
										p: 0,
										m: '0 0 0 0',
										backgroundColor: isDark ? '#161616' : '#fff !important',
										color: isDark ? '#fff' : '#000',
									}}
								>
									<AccountCheckboxContainer
										requiredAccounts={requiredAccounts}
										isZero={false}
										start={accordionAccountsDetails}
										end={requiredAccounts.length}
										accountRestrictions={props.accountRestrictions}
										{...props}
									/>
									<Box textAlign={'start'} mt={'0.5rem'}>
										<ButtonBase
											sx={{
												...sizeFontCustom.heading,
												color: !isActiveFlag
													? 'lightgray'
													: (props.customDetails?.primaryTextColor ??
														(isDark ? '#fff' : theme.palette.primary.main)),
												display: 'flex',
												alignItems: 'center',
												gap: '0.25rem',
											}}
											onClick={() => setIsExpanded(false)}
										>
											{t('common.buttons.showLess')}
											<ExpandMoreIcon
												sx={{ fontSize: '1rem', transform: 'rotate(180deg)' }}
											/>
										</ButtonBase>
									</Box>
								</AccordionDetails>
							</Accordion>
							{accountsLinkedFlag &&
								!global.hideAddNew &&
								!(
									global.redirectAccountRestrictions &&
									global.sdkOptions?.disabledAddNew === 'true'
								) && (
									<AddNewButton
										startIcon={
											<AddIcon sx={{ height: '1.25rem', width: '1.25rem' }} />
										}
										isActiveFlag={isActiveFlag}
										handleAddAccount={handleAddAccount}
										buttonStyles={{ marginTop: '1rem' }}
									/>
								)}
						</>
					) : (
						<>
							<AccountCheckboxContainer
								requiredAccounts={requiredAccounts}
								isZero={false}
								start={0}
								end={accordionAccountsDetails}
								accountRestrictions={props.accountRestrictions}
								{...props}
							/>
							{accountsLinkedFlag &&
								!global.hideAddNew &&
								!(
									global.redirectAccountRestrictions &&
									global.sdkOptions?.disabledAddNew === 'true'
								) && (
									<AddNewButton
										startIcon={
											<AddIcon sx={{ height: '1.25rem', width: '1.25rem' }} />
										}
										isActiveFlag={isActiveFlag}
										handleAddAccount={handleAddAccount}
										buttonStyles={{ marginTop: '1rem' }}
									/>
								)}
						</>
					)}
					{/* For fiTypes having 0 linked accounts */}
					{!accountsLinkedFlag &&
						!global.hideAddNew &&
						fIGroupsWithZeroAccts?.length && (
						<Accordion
							elevation={0}
							expanded={isExpanded}
							sx={{
								...linkedAccountsFIStyles.accordian,
								backgroundColor: isDark ? 'transparent' : '#fff',
								'&::before': {
									display: 'none',
								},
								'&::after': {
									display: 'none',
								},
								// Target the specific MUI classes you found
								'&.MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPaper-elevation0.MuiAccordion-root.MuiAccordion-rounded.MuiAccordion-gutters':
									{
										backgroundColor: isDark ? 'transparent' : '#fff !important',
									},
								'& .MuiButtonBase-root.MuiAccordionSummary-root.MuiAccordionSummary-gutters':
									{
										backgroundColor: isDark ? 'transparent' : '#fff !important',
									},
								'& .MuiAccordionSummary-content.MuiAccordionSummary-contentGutters':
									{
										backgroundColor: isDark ? 'transparent' : '#fff !important',
									},
								'& .MuiAccordionDetails-root': {
									backgroundColor: isDark ? 'transparent' : '#fff !important',
								},
							}}
						>
							<AccordionSummary
								onClick={() => {}}
								sx={{
									...linkedAccountsFIStyles.accordianSummary,
									backgroundColor: isDark ? 'transparent' : '#fff !important',
									color: isDark ? '#fff' : '#000',
									// Target the specific classes for AccordionSummary
									'&.MuiButtonBase-root.MuiAccordionSummary-root.MuiAccordionSummary-gutters':
										{
											backgroundColor: isDark
												? 'transparent'
												: '#fff !important',
										},
									'& .MuiAccordionSummary-content.MuiAccordionSummary-contentGutters':
										{
											backgroundColor: isDark
												? 'transparent'
												: '#fff !important',
										},
									'&:hover': {
										backgroundColor: isDark ? 'transparent' : '#fff !important',
									},
									'&.Mui-expanded': {
										backgroundColor: isDark ? 'transparent' : '#fff !important',
									},
								}}
								expandIcon={
									!isExpanded &&
									fIGroupsWithZeroAccts?.length > accordionAccountsDetails && (
										<ButtonBase
											disabled={!isActiveFlag}
											sx={{
												...sizeFontCustom.altHeading,
												color: !isActiveFlag
													? 'lightgray'
													: (props.customDetails?.primaryTextColor ??
														(isDark ? '#fff' : theme.palette.primary.main)),
											}}
											onClick={() => setIsExpanded(true)}
											color={'primary'}
										>
											{`+ ${
												fIGroupsWithZeroAccts?.length - accordionAccountsDetails
											} ${t('common.buttons.showMore')}`}
										</ButtonBase>
									)
								}
								aria-controls="noAccountsLinkedPanel-content"
								id="noAccountsLinkedPanel-header"
							>
								<AccountCheckboxContainer
									requiredAccounts={fIGroupsWithZeroAccts}
									isZero={true}
									start={0}
									end={accordionAccountsDetails}
									accountRestrictions={props.accountRestrictions}
									{...props}
								/>
							</AccordionSummary>
							<AccordionDetails
								sx={{
									p: 0,
									m: '0 0 0 0',
									backgroundColor: isDark ? '#161616' : '#fff !important',
									color: isDark ? '#fff' : '#000',
								}}
							>
								<AccountCheckboxContainer
									requiredAccounts={fIGroupsWithZeroAccts}
									isZero={true}
									start={accordionAccountsDetails}
									end={fIGroupsWithZeroAccts?.length}
									accountRestrictions={props.accountRestrictions}
									{...props}
								/>
								<Box textAlign={'start'} mt={'0.5rem'}>
									<ButtonBase
										sx={{
											...sizeFontCustom.heading,
											color: !isActiveFlag
												? 'lightgray'
												: (props.customDetails?.primaryTextColor ??
													(isDark ? '#fff' : theme.palette.primary.main)),
										}}
										onClick={() => setIsExpanded(false)}
									>
										{t('common.buttons.showLess')}
									</ButtonBase>
								</Box>
							</AccordionDetails>
						</Accordion>
					)}
				</>
			</Box>
		</Paper>
	);
}

export default LinkedAccountsFI;
